import React, { useState, useEffect } from "react";
import { networkAPI } from "../lib/api";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import { Button } from "../components/ui/button";
import {
  Network,
  ArrowRight,
  Clock,
  Activity,
  Globe,
  Server,
} from "lucide-react";
import { toast } from "sonner";

export const NetworkPage = () => {
  const [traffic, setTraffic] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  useEffect(() => {
    fetchTraffic();
    const interval = setInterval(() => fetchTraffic(), 30000);
    return () => clearInterval(interval);
  }, [currentPage]);

  const fetchTraffic = async (page = currentPage) => {
    try {
      const response = await networkAPI.getAll(page, 20);
      setTraffic(response.items);
      setTotalPages(response.total_pages || 1);
    } catch (err) {
      toast.error("Failed to fetch network traffic");
    } finally {
      setLoading(false);
    }
  };

  const formatBytes = (bytes) => {
    if (bytes === 0) return "0 B";
    const k = 1024;
    const sizes = ["B", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
  };

  const formatTimeAgo = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMins / 60);

    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    return `${Math.floor(diffHours / 24)}d ago`;
  };

  const getProtocolColor = (protocol) => {
    switch (protocol.toLowerCase()) {
      case "https":
        return "bg-green-100 text-green-800";
      case "ssh":
        return "bg-red-100 text-red-800";
      case "mysql":
        return "bg-blue-100 text-blue-800";
      case "dns":
        return "bg-purple-100 text-purple-800";
      default:
        return "bg-slate-100 text-slate-800";
    }
  };

  const isExternalIP = (ip) => {
    if (!ip) return false;
    if (ip.startsWith("192.168.") || ip.startsWith("10.") || ip === "127.0.0.1" || ip.startsWith("::") || ip === "0.0.0.0") return false;
    // 172.16.0.0 - 172.31.255.255
    if (ip.startsWith("172.")) {
      const second = parseInt(ip.split(".")[1], 10);
      if (second >= 16 && second <= 31) return false;
    }
    // 169.254.x.x (link-local)
    if (ip.startsWith("169.254.")) return false;
    return true;
  };

  const totalBytes = traffic.reduce((acc, t) => acc + t.bytes_transferred, 0);
  const totalPackets = traffic.reduce((acc, t) => acc + t.packets, 0);
  const externalConnections = traffic.filter(
    (t) => isExternalIP(t.source_ip) || isExternalIP(t.destination_ip),
  ).length;

  if (loading) {
    return (
      <div
        className="flex items-center justify-center h-64"
        data-testid="network-loading"
      >
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="network-page">
      {/* Page Header */}
      <div>
        <h1 className="font-heading text-3xl font-bold tracking-tight text-foreground">
          Network
        </h1>
        <p className="text-muted-foreground mt-1">
          Monitor network traffic and connections
        </p>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card
          className="border-border shadow-sm"
          data-testid="connections-card"
        >
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">
                  Total Connections
                </p>
                <p className="font-heading text-3xl font-bold">
                  {traffic.length}
                </p>
              </div>
              <div className="p-3 rounded-full bg-primary/10">
                <Network className="w-6 h-6 text-primary" />
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="border-border shadow-sm" data-testid="traffic-card">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">
                  Data Transferred
                </p>
                <p className="font-heading text-3xl font-bold">
                  {formatBytes(totalBytes)}
                </p>
              </div>
              <div className="p-3 rounded-full bg-blue-100">
                <Activity className="w-6 h-6 text-blue-600" />
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="border-border shadow-sm" data-testid="packets-card">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">
                  Total Packets
                </p>
                <p className="font-heading text-3xl font-bold">
                  {totalPackets.toLocaleString()}
                </p>
              </div>
              <div className="p-3 rounded-full bg-purple-100">
                <Server className="w-6 h-6 text-purple-600" />
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="border-border shadow-sm" data-testid="external-card">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">
                  External Connections
                </p>
                <p className="font-heading text-3xl font-bold">
                  {externalConnections}
                </p>
              </div>
              <div className="p-3 rounded-full bg-amber-100">
                <Globe className="w-6 h-6 text-amber-600" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Traffic Table */}
      <Card className="border-border shadow-sm">
        <CardHeader>
          <CardTitle className="font-heading text-lg font-semibold">
            Recent Traffic ({traffic.length})
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Connection</TableHead>
                  <TableHead>Protocol</TableHead>
                  <TableHead>Data</TableHead>
                  <TableHead>Packets</TableHead>
                  <TableHead>Time</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {traffic.length === 0 ? (
                  <TableRow>
                    <TableCell
                      colSpan={5}
                      className="text-center py-8 text-muted-foreground"
                    >
                      No network traffic found
                    </TableCell>
                  </TableRow>
                ) : (
                  traffic.map((item) => (
                    <TableRow
                      key={item.id}
                      data-testid={`traffic-row-${item.id}`}
                    >
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <div className="flex items-center gap-2">
                            {isExternalIP(item.source_ip) ? (
                              <Globe className="w-4 h-4 text-amber-500" />
                            ) : (
                              <Server className="w-4 h-4 text-slate-400" />
                            )}
                            <code className="ip-address">{item.source_ip}</code>
                          </div>
                          <ArrowRight className="w-4 h-4 text-muted-foreground" />
                          <div className="flex items-center gap-2">
                            {isExternalIP(item.destination_ip) ? (
                              <Globe className="w-4 h-4 text-amber-500" />
                            ) : (
                              <Server className="w-4 h-4 text-slate-400" />
                            )}
                            <code className="ip-address">
                              {item.destination_ip}
                            </code>
                          </div>
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge className={`${getProtocolColor(item.protocol)}`}>
                          {item.protocol}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <span className="font-mono text-sm">
                          {formatBytes(item.bytes_transferred)}
                        </span>
                      </TableCell>
                      <TableCell>
                        <span className="text-sm">
                          {item.packets.toLocaleString()}
                        </span>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1 text-sm text-muted-foreground">
                          <Clock className="w-3 h-3" />
                          {formatTimeAgo(item.timestamp)}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      {totalPages > 1 && (
        <div className="flex items-center justify-center gap-2">
          <Button variant="outline" size="sm" disabled={currentPage <= 1} onClick={() => { setCurrentPage(p => p - 1); }}>
            Previous
          </Button>
          <span className="text-sm text-muted-foreground">
            Page {currentPage} of {totalPages}
          </span>
          <Button variant="outline" size="sm" disabled={currentPage >= totalPages} onClick={() => { setCurrentPage(p => p + 1); }}>
            Next
          </Button>
        </div>
      )}

      {/* Protocol Breakdown */}
      <Card className="border-border shadow-sm">
        <CardHeader>
          <CardTitle className="font-heading text-lg font-semibold">
            Protocol Breakdown
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            {[...new Set(traffic.map((t) => t.protocol))].map((protocol) => {
              const protocolTraffic = traffic.filter(
                (t) => t.protocol === protocol,
              );
              const bytes = protocolTraffic.reduce(
                (acc, t) => acc + t.bytes_transferred,
                0,
              );
              return (
                <div
                  key={protocol}
                  className="p-4 rounded-lg border border-border"
                  data-testid={`protocol-${protocol.toLowerCase()}`}
                >
                  <Badge className={`${getProtocolColor(protocol)} mb-2`}>
                    {protocol}
                  </Badge>
                  <p className="font-heading text-2xl font-bold">
                    {protocolTraffic.length}
                  </p>
                  <p className="text-sm text-muted-foreground">
                    {formatBytes(bytes)}
                  </p>
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};
